import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import Logo from '../../components/Logo';
import ThemeToggle from '../../components/ThemeToggle';
import Footer from '../../components/Footer';
import { Mail, Lock, Loader2, ShieldCheck, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

// Helper function to safely extract error message from API response
const getErrorMessage = (error) => {
    try {
        const detail = error?.response?.data?.detail;
        
        // If detail is a string, return it directly
        if (typeof detail === 'string') {
            return detail;
        }
        
        // If detail is an array (Pydantic validation errors), extract messages
        if (Array.isArray(detail)) {
            const messages = detail.map(err => {
                if (typeof err === 'string') return err;
                if (err?.msg) return err.msg;
                if (err?.message) return err.message;
                return 'Validation error';
            });
            return messages.join('. ');
        }
        
        // If detail is an object, try to extract message
        if (typeof detail === 'object' && detail !== null) {
            if (detail.msg) return detail.msg;
            if (detail.message) return detail.message;
            return JSON.stringify(detail);
        }
        
        // Fallback to error message or default
        return error?.message || 'Admin login failed. Please try again.';
    } catch {
        return 'Admin login failed. Please try again.';
    }
};

const AdminLogin = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const { loginWithToken } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError(''); // Clear error when user types
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Basic validation
        if (!formData.email || !formData.password) {
            setError('Please enter both email and password.');
            setLoading(false);
            return;
        }

        try {
            // Use the admin-specific login endpoint
            const response = await api.post('/admin/auth/login', formData);
            const { access_token, user } = response.data;
            
            // Store token and user data using direct login method
            loginWithToken(access_token, user);
            
            toast.success('Admin login successful');
            navigate('/admin');
        } catch (err) {
            // Safely extract error message
            const errorMessage = getErrorMessage(err);
            
            // Map common errors to user-friendly messages
            let displayMessage = errorMessage;
            if (errorMessage.toLowerCase().includes('invalid') || 
                errorMessage.toLowerCase().includes('credentials')) {
                displayMessage = 'Invalid admin credentials. Please check your email and password.';
            } else if (errorMessage.toLowerCase().includes('admin access')) {
                displayMessage = 'This account does not have admin access.';
            } else if (errorMessage.toLowerCase().includes('disabled') || 
                       errorMessage.toLowerCase().includes('deactivated')) {
                displayMessage = 'This admin account has been disabled.';
            }
            
            setError(displayMessage);
            toast.error(displayMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Theme Toggle - Top Right */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="flex items-center justify-center mb-8">
                        <Logo variant="full" size="large" />
                    </Link>

                    <Card className="bg-card border-border shadow-lg">
                        <CardHeader className="text-center pb-4">
                            <div className="flex justify-center mb-4">
                                <div className="p-4 rounded-full bg-primary/10 border border-primary/20">
                                    <ShieldCheck className="h-10 w-10 text-primary" />
                                </div>
                            </div>
                            <CardTitle className="font-heading font-bold text-2xl text-foreground">
                                Admin Access
                            </CardTitle>
                            <CardDescription className="text-muted-foreground">
                                Secure login for administrators and moderators only
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {/* Error Display */}
                            {error && (
                                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-destructive">{error}</p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email">Admin Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="admin@clientcheck.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12"
                                            required
                                            autoComplete="email"
                                            data-testid="admin-login-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="pl-10 pr-10 bg-accent border-border h-12"
                                            required
                                            autoComplete="current-password"
                                            data-testid="admin-login-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                            tabIndex={-1}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 text-base font-semibold active:scale-[0.98] transition-transform"
                                    disabled={loading}
                                    data-testid="admin-login-submit"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Verifying credentials...
                                        </>
                                    ) : (
                                        <>
                                            <ShieldCheck className="mr-2 h-5 w-5" />
                                            Admin Sign In
                                        </>
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 pt-6 border-t border-border text-center">
                                <p className="text-sm text-muted-foreground">
                                    Not an admin?{' '}
                                    <Link 
                                        to="/login" 
                                        className="text-primary hover:underline font-medium"
                                    >
                                        Contractor login
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="mt-4 p-3 rounded-lg bg-accent/30 border border-border">
                        <p className="text-center text-xs text-muted-foreground">
                            <ShieldCheck className="inline h-3 w-3 mr-1" />
                            This portal is restricted to authorized personnel only.
                            <br />
                            Unauthorized access attempts are logged.
                        </p>
                    </div>
                </div>
            </div>
            
            <Footer />
        </div>
    );
};

export default AdminLogin;
